import { ElementRef, Injectable, QueryList, inject } from '@angular/core';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockContentType, TimeBlockType } from '../types/time-block-types';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeBlockAbsenceModel } from '../../../../../core/models/timeblock/time-block.model';
import { makeSafeForCSS } from '../../../../../shared/functions/string-functions';
import { CalendarService } from '../../../services/calendar.service';
import { CSSHasNextPart, CSSHasPrevPart } from '../../../util/calendar-style.constants';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';

@Injectable({
  providedIn: 'root',
})
export class TimeBlockCSSService {
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly calendarService = inject(CalendarService);

  public configureFixIntervalOverflowIndicators(
    timeBlockComponentItem: ITimeBlockComponentItem,
    overflowIndicators: QueryList<ElementRef<HTMLElement>>,
  ): void {
    const timeBlockModel = timeBlockComponentItem.timeBlockModel;
    if (
      timeBlockModel.partNumber === 0 &&
      timeBlockModel.fixInterval !== null &&
      timeBlockModel.type === TimeBlockType.ExistingBlock
    ) {
      const calendarModel =
        this.calendarService.getCalendarModelByTimeBlockType(timeBlockComponentItem);
      if (
        DateTimeHelper.isBefore(
          timeBlockComponentItem.timeBlockModel.start,
          calendarModel.calendarProperties.visibleStartDate,
        )
      ) {
        const leftIndicatorHTMLEl = overflowIndicators.first.nativeElement;
        this.shiveRenderService.removeClass(leftIndicatorHTMLEl, 'k-d-none');
      }

      if (
        DateTimeHelper.isAfter(
          timeBlockComponentItem.timeBlockModel.end,
          calendarModel.calendarProperties.visibleEndDate,
        )
      ) {
        const rightIndicatorHTMLEl = overflowIndicators.last.nativeElement;
        this.shiveRenderService.removeClass(rightIndicatorHTMLEl, 'k-d-none');
      }
    }
  }

  /**
   * Previous and next classes apply only for day and week inner day time blocks.
   */
  public setTimeBlockPrevNextCSSClasses(
    timeBlockComponentItem: ITimeBlockComponentItem,
    timeBlockHtmlWrapper: ElementRef<HTMLElement>,
  ): void {
    const partNr = timeBlockComponentItem.timeBlockModel.partNumber;
    const partCount = timeBlockComponentItem.timeBlockModel.partCount;
    if (partNr === 0) {
      this.shiveRenderService.removeClass(timeBlockHtmlWrapper.nativeElement, CSSHasPrevPart);
    }

    if (partNr === partCount - 1) {
      this.shiveRenderService.removeClass(timeBlockHtmlWrapper.nativeElement, CSSHasNextPart);
    }
  }

  public getTimeBlockContentTypeClass(timeBlockComponentItem: ITimeBlockComponentItem): string {
    if (timeBlockComponentItem.timeBlockContentType === TimeBlockContentType.Absence) {
      const absenceReason = (timeBlockComponentItem.timeBlockModel as TimeBlockAbsenceModel).reason;
      return typeof absenceReason !== 'string' ? '' : makeSafeForCSS(absenceReason);
    }

    return '';
  }

  public isAbsenceWorkingTimeClass(timeBlockComponentItem: ITimeBlockComponentItem): boolean {
    return (
      timeBlockComponentItem.timeBlockContentType === TimeBlockContentType.Absence &&
      (timeBlockComponentItem.timeBlockModel as TimeBlockAbsenceModel).reason.isWorkingTime
    );
  }
}
